import React, { useMemo } from 'react';
import { Link as BaseLink, Box, Button } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ColumnDef,
} from 'material-react-table';
import { Link } from 'react-router-dom';

import { formatDate } from '../../helpers';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMaterialReactTableLocalization, useVocabulary } from '../../i18n';

interface Site {
  id: string;
  name: string;
  createdAt: Date;
  shared: boolean;
  siteType: string;
}

const gridStyles = { marginBottom: 6, display: 'grid' };

export default function SiteGrid({ rows, loading = false }) {
  const intl = useIntl();
  const vocabulary = useVocabulary();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo<MRT_ColumnDef<Site>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 100,
        Cell: ({ renderedCellValue, cell }) => (
          <BaseLink
            underline="none"
            component={Link}
            to={`/s/${cell.getValue()}`}
          >
            <span>{renderedCellValue}</span>
          </BaseLink>
        ),
      },
      {
        accessorKey: 'name',
        header: intl.formatMessage({
          id: 'site.field.name',
          defaultMessage: 'Site name',
        }),
        size: 180,
      },
      {
        id: 'shared',
        accessorFn: (row) =>
          row.shared
            ? intl.formatMessage({
                id: 'site.ownership.sharedWithMe',
                defaultMessage: 'Shared with me',
              })
            : intl.formatMessage({
                id: 'site.ownership.mine',
                defaultMessage: 'My site',
              }),
        header: intl.formatMessage({
          id: 'site.ownership.title',
          defaultMessage: 'Ownership',
        }),
        size: 120,
      },
      {
        id: 'siteType',
        accessorFn: (row) => vocabulary('siteType', row.siteType) || '-',
        header: intl.formatMessage({
          id: 'form.type',
          defaultMessage: 'Type',
        }),
        size: 180,
      },
      {
        Cell: ({ cell }) => formatDate(cell.getValue<Date | null>()) || '-',
        accessorFn: (row) => row.createdAt && new Date(row.createdAt),
        header: intl.formatMessage({
          id: 'site.dateCreated',
          defaultMessage: 'Date created',
        }),
        size: 120,
      },
    ],
    [intl, vocabulary]
  );
  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization,
    enableRowSelection: true,
    enableToolbarInternalActions: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      density: 'compact',
    },
    state: { isLoading: loading },
    muiPaginationProps: {
      showRowsPerPage: false,
    },
    muiTablePaperProps: {
      sx: gridStyles,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: '1rem', p: '4px' }}>
        <Button
          component={Link}
          to="create"
          color="primary"
          variant="contained"
        >
          <FormattedMessage
            id="site.action.create"
            defaultMessage="Create site"
          />
        </Button>
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
}
